import React from 'react';

/** The subset of `navigator` the tablet detector reads. Injectable so the pure function
 *  can be unit-tested with a fabricated user-agent, with no DOM. */
export interface NavigatorLike {
  userAgent: string;
  maxTouchPoints?: number;
}

/**
 * Whether the current device is a tablet (iPad, Android tablet, or another touch tablet).
 *
 * The desktop configurator floats heavy, persistent preview `<iframe>`s that re-measure
 * on every step navigation; on iPad Safari that measurement feedback loop exhausts
 * React's update budget ("Maximum update depth exceeded") and blanks the wizard. On
 * tablets we render the pre-rendered screenshots instead (exactly as the mobile flow
 * already does), so this gate decides which path each preview surface takes.
 *
 * Pure and injectable — pass a {@link NavigatorLike} to test; the default reads the live
 * `navigator` when present (SSR-safe, returns false without one).
 */
export function isTabletUserAgent(
  nav: NavigatorLike | undefined = typeof navigator !== 'undefined' ? navigator : undefined,
): boolean {
  if (!nav) return false;
  const ua = nav.userAgent || '';
  const touchPoints = typeof nav.maxTouchPoints === 'number' ? nav.maxTouchPoints : 0;

  // Classic iPad UA (older iPadOS, and the iOS Simulator's default iPad user-agent).
  if (/\biPad\b/i.test(ua)) return true;

  // iPadOS 13+ Safari reports the desktop macOS user-agent; the only reliable tell from a
  // real Mac (which is never a touchscreen) is a multi-touch pointer.
  if (/\bMacintosh\b/.test(ua) && touchPoints > 1) return true;

  // Android tablets identify as "Android" WITHOUT the "Mobile" token that phones carry.
  if (/\bAndroid\b/.test(ua) && !/\bMobile\b/.test(ua)) return true;

  // Other explicit tablet tokens (BlackBerry PlayBook, Kindle Silk, generic "Tablet").
  if (/\b(Tablet|PlayBook|Silk|Kindle)\b/i.test(ua)) return true;

  return false;
}

/**
 * React hook form of {@link isTabletUserAgent}. Device class is fixed for the session, so
 * it is read ONCE via a lazy `useState` initializer (SSR-safe) and never recomputed —
 * unlike a viewport media query, it cannot flip mid-session.
 */
export function useIsTablet(): boolean {
  const [isTablet] = React.useState(() => isTabletUserAgent());
  return isTablet;
}
